import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Filter, RefreshCw, Link2 } from 'lucide-react';
import { patientsApi } from '../../api/patients';
import { PatientTable } from '../../components/patients/PatientTable';
import { PatientSearchBar } from '../../components/patients/PatientSearchBar';
import { LinkPatientModal } from '../../components/patients/LinkPatientModal';
import type { PaginatedPatients, PatientQueryParams, PatientSearchResult } from '../../types/patient';

export function PatientsListPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedPatients | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [queryParams, setQueryParams] = useState<PatientQueryParams>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const loadPatients = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: PatientQueryParams = {
        ...queryParams,
        includeInactiveInSite: showInactive,
      };
      const result = await patientsApi.getAll(params);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Error al cargar pacientes');
    } finally {
      setIsLoading(false);
    }
  }, [queryParams, showInactive]);

  useEffect(() => { loadPatients(); }, [loadPatients]);

  // Guardar IDs en sessionStorage para la navegación ◀ ▶ de PatientDetailPage
  useEffect(() => {
    if (data?.data) {
      try {
        const ids = data.data.map((p) => p.id);
        sessionStorage.setItem('imi_patients_list_ids', JSON.stringify(ids));
      } catch {
        /* sessionStorage no disponible: ignorar */
      }
    }
  }, [data]);

  const handlePageChange = (page: number) => setQueryParams((prev) => ({ ...prev, page }));
  const handleSearch = (patient: PatientSearchResult) => navigate(`/pacientes/${patient.id}`);

  const handlePatientLinked = (patientId: string) => {
    loadPatients();
    navigate(`/pacientes/${patientId}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pacientes</h1>
          <p className="text-sm text-gray-500 mt-1">Gestión de pacientes de la sede</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowLinkModal(true)} 
            className="inline-flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Link2 className="h-5 w-5 mr-2" /> Vincular existente
          </button>
          <button 
            onClick={() => navigate('/pacientes/nuevo')} 
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" /> Nuevo paciente
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1"><PatientSearchBar onSelect={handleSearch} /></div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters(!showFilters)} className={`p-2 rounded-lg border transition-colors ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`} title="Filtros">
              <Filter className="h-5 w-5" />
            </button>
            <button onClick={loadPatients} disabled={isLoading} className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50" title="Actualizar">
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input 
                type="checkbox" 
                checked={showInactive} 
                onChange={(e) => { 
                  setShowInactive(e.target.checked); 
                  setQueryParams((p) => ({ ...p, page: 1 })); 
                }} 
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
              />
              <span className="text-gray-700">Mostrar inactivos en este centro</span>
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Ordenar:</label>
              <select value={queryParams.sortBy} onChange={(e) => setQueryParams((p) => ({ ...p, sortBy: e.target.value as any, page: 1 }))} className="text-sm border border-gray-300 rounded-lg px-2 py-1">
                <option value="createdAt">Fecha alta</option>
                <option value="updatedAt">Última modificación</option>
              </select>
              <select value={queryParams.sortOrder} onChange={(e) => setQueryParams((p) => ({ ...p, sortOrder: e.target.value as any, page: 1 }))} className="text-sm border border-gray-300 rounded-lg px-2 py-1">
                <option value="desc">Más reciente</option>
                <option value="asc">Más antiguo</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Mostrar:</label>
              <select value={queryParams.limit} onChange={(e) => setQueryParams((p) => ({ ...p, limit: parseInt(e.target.value, 10), page: 1 }))} className="text-sm border border-gray-300 rounded-lg px-2 py-1">
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
              <span className="text-sm text-gray-600">por página</span>
            </div>
          </div>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      {data && <PatientTable data={data} onPageChange={handlePageChange} onRefresh={loadPatients} isLoading={isLoading} />}

      <LinkPatientModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onSuccess={handlePatientLinked}
      />
    </div>
  );
}
